import { motion } from "framer-motion";
import { FileText, Download, Calendar, ExternalLink } from "lucide-react";

const AnnualReports = () => {
  const reports = [
    {
      year: "2025",
      title: "Auditors Report 31/03/2025",
      description: "Auditor's report for the fiscal year ending 31st March 2025 covering financial statements, compliance, and organisational audit findings.",
      url: "/annual-reports/DOC-2.pdf",
      comingSoon: false,
    },
    {
      year: "2025-26",
      title: "Annual Report 2025-26",
      description: "A comprehensive overview of PAHAD Trust's programmes, community impact, financial statements, and milestones achieved during the fiscal year.",
      url: "/annual-reports/DOC-5.pdf",
      comingSoon: false,
    },
    {
      year: "2024-25",
      title: "Annual Report 2024-25",
      description: "A comprehensive overview of PAHAD Trust's programmes, community impact, financial statements, and milestones achieved during the fiscal year.",
      url: "/annual-reports/DOC-4.pdf",
      comingSoon: false,
    },
    {
      year: "2024",
      title: "Auditor Report 31/03/2024",
      description: "Auditor's report for the fiscal year ending 31st March 2024 covering financial statements, compliance, and organisational audit findings.",
      url: "/annual-reports/DOC-1.pdf",
      comingSoon: false,
    },
    {
      year: "2023",
      title: "Auditor Report 31/03/2023",
      description: "Auditor's report for the fiscal year ending 31st March 2023 covering financial statements, compliance, and organisational audit findings.",
      url: "/annual-reports/DOC-3.pdf",
      comingSoon: false,
    },
    {
      year: "2021-24",
      title: "Three Year Narrative Report (April 2021 - March 2024)",
      description: "A three-year narrative report covering PAHAD Trust's programmes, impact stories, and developmental milestones from April 2021 to March 2024.",
      url: "/annual-reports/DOC-6.pdf",
      comingSoon: false,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1568667256549-094345857637?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxtb3VudGFpbiUyMGRvY3VtZW50cyUyMHBhcGVyJTIwaGltYWxheWFzfGVufDB8fHx8MTc3NDc2MzY5N3ww&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/50" />
        </div>
        <div className="relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
          >
            Annual Reports
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-lg text-white/80 max-w-2xl mx-auto"
          >
            Transparency in our work, accountability to our communities
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-[#1C221F] mb-6">
              Our Commitment to Transparency
            </h2>
            <p className="text-base leading-relaxed text-[#515E58] max-w-3xl mx-auto">
              PAHAD Trust believes in complete transparency with our stakeholders, partners, and the communities we serve. Our annual reports document our journey, impact, financial stewardship, and the milestones we achieve together in empowering Himalayan communities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reports List */}
      <section className="py-16 bg-[#F9F8F6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {reports.map((report, index) => (
              <motion.div
                key={report.year}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#E4E2DC]/40 hover:shadow-md transition-shadow"
              >
                <div className="p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-[#2B5C45]/10 flex items-center justify-center">
                    <FileText className="h-10 w-10 text-[#2B5C45]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#8C9A74]/15 text-[#2B5C45]">
                        <Calendar className="h-3 w-3 mr-1" />
                        {report.year}
                      </span>
                    </div>
                    <h3 className="text-xl font-heading font-medium text-[#1C221F] mb-2">
                      {report.title}
                    </h3>
                    <p className="text-sm text-[#515E58] leading-relaxed">
                      {report.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {report.comingSoon ? (
                      <span className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-medium bg-[#E4E2DC]/50 text-[#515E58] cursor-not-allowed">
                        Coming Soon
                      </span>
                    ) : (
                      <a
                        href={report.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-medium bg-[#2B5C45] text-white hover:bg-[#1C3D2E] transition-colors"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#2B5C45]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-heading font-medium text-white mb-4">
              Want to Learn More?
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              For detailed information about our work, financials, or to request specific documents, please reach out to us.
            </p>
            <a
              href="/donate"
              className="inline-flex items-center px-6 py-3 rounded-lg text-sm font-medium bg-white text-[#2B5C45] hover:bg-[#F9F8F6] transition-colors"
            >
              Contact Us
              <ExternalLink className="h-4 w-4 ml-2" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AnnualReports;
